import { cmsBlockLinks, cmsLinkedDocument, cmsLinkedPage } from '@shared/cms/links';
import type { SiteCmsBlock, SiteCmsDraftResponse, SiteCmsMenuItem } from '@shared/types/entities';

import { cmsIsSafeLink } from './site-cms-interaction-model';

const ids = new WeakMap<object, string>();
let counter = 0;
/** Session-only keys never reach Prisma or a public snapshot. */
export function cmsIdentity(value: object): string {
  let id = ids.get(value);
  if (!id) {
    id = `cms-${++counter}`;
    ids.set(value, id);
  }
  return id;
}
export function cmsKeepIdentity<T extends object>(before: object, after: T): T {
  ids.set(after, cmsIdentity(before));
  return after;
}
/** Keep UI identities on a normalized ACK when the submitted structure is unchanged. */
export function cmsAdoptDraftIdentities(
  before: SiteCmsDraftResponse,
  after: SiteCmsDraftResponse,
): SiteCmsDraftResponse {
  const menu = (old: SiteCmsMenuItem[], next: SiteCmsMenuItem[]): SiteCmsMenuItem[] =>
    next.map((item, index) => {
      const previous = old[index];
      const copy = { ...item, children: menu(previous?.children ?? [], item.children) };
      return previous && previous.type === item.type ? cmsKeepIdentity(previous, copy) : copy;
    });
  return {
    ...after,
    headerMenu: menu(before.headerMenu, after.headerMenu),
    pages: after.pages.map(page => {
      const previous = before.pages.find(old => old.slug === page.slug);
      return {
        ...page,
        blocks: page.blocks.map((block, index) =>
          previous?.blocks[index]?.type === block.type ? cmsKeepIdentity(previous.blocks[index], block) : block,
        ),
      };
    }),
  };
}
export type CmsStructureUndo = { before: SiteCmsBlock[]; added: Set<string> };
export function cmsStructureCheckpoint(before: SiteCmsBlock[], after: SiteCmsBlock[]): CmsStructureUndo {
  const known = new Set(before.map(cmsIdentity));
  return { before, added: new Set(after.map(cmsIdentity).filter(id => !known.has(id))) };
}
export function undoCmsStructure(current: SiteCmsBlock[], undo: CmsStructureUndo): SiteCmsBlock[] {
  const byId = new Map(current.map(block => [cmsIdentity(block), block]));
  const oldIds = new Set(undo.before.map(cmsIdentity));
  // Only undo order/insertion/removal. Never restore obsolete text for a surviving block.
  return [
    ...undo.before.map(block => byId.get(cmsIdentity(block)) ?? block),
    ...current.filter(block => !oldIds.has(cmsIdentity(block)) && !undo.added.has(cmsIdentity(block))),
  ];
}
export function cmsMenuCount(items: readonly SiteCmsMenuItem[]): number {
  return items.reduce((n, item) => n + 1 + cmsMenuCount(item.children), 0);
}
export function normalizeCmsMenu(items: readonly SiteCmsMenuItem[]): SiteCmsMenuItem[] {
  return items.flatMap(item => {
    const children = normalizeCmsMenu(item.children);
    if (item.type === 'GROUP' && !children.length) return [];
    return [cmsKeepIdentity(item, { ...item, children })];
  });
}
export function cmsChangeMenuType(
  item: SiteCmsMenuItem,
  type: SiteCmsMenuItem['type'],
  documentSlug = '',
): SiteCmsMenuItem[] {
  let changed: SiteCmsMenuItem;
  if (type === 'PAGE') changed = { type, label: item.label, slug: 'home', children: item.children };
  else if (type === 'DOCUMENT') changed = { type, label: item.label, slug: documentSlug, children: item.children };
  else if (type === 'GROUP')
    changed = {
      type,
      label: item.label,
      children: item.children.length ? item.children : [{ type: 'PAGE', label: 'Accueil', slug: 'home', children: [] }],
    };
  else changed = { type, label: item.label, url: 'https://boulesconnect.fr/', children: [] };
  // External links cannot have children; promote rather than destroy them.
  return [cmsKeepIdentity(item, changed), ...(type === 'EXTERNAL_LINK' ? item.children : [])];
}
export function cmsReviewKey(draft: SiteCmsDraftResponse, resources: unknown): string {
  return JSON.stringify([
    draft.ownerType,
    draft.ownerId,
    draft.revision,
    draft.updatedAt,
    draft.title,
    draft.description,
    draft.theme,
    draft.branding,
    draft.pages,
    draft.headerMenu,
    resources,
  ]);
}
export function cmsSectionSummary(block: SiteCmsBlock): string {
  if (block.type === 'heading' || block.type === 'paragraph') return block.text.slice(0, 130);
  if (block.type === 'button') return `${block.label} → ${block.href}`;
  if (block.type === 'documents') return `${block.title} · ${block.slugs.length} PDF`;
  if (block.type === 'competitions') return `${block.title} · ${block.ids.length} concours`;
  if (block.type === 'partners' || block.type === 'info') return `${block.title} · ${block.items.length} éléments`;
  return block.title;
}
export type CmsIssue = {
  id: string;
  message: string;
  pageSlug?: string;
  blockIndex?: number;
  section?: 'navigation' | 'settings' | 'design';
  publicationOnly?: boolean;
};
export function cmsDraftIssues(draft: SiteCmsDraftResponse): CmsIssue[] {
  const issues: CmsIssue[] = [];
  const add = (issue: Omit<CmsIssue, 'id'>) => issues.push({ id: `issue-${issues.length}`, ...issue });
  if (!draft.title.trim()) add({ section: 'settings', message: 'Donnez un nom à votre site.' });
  if (!draft.description.trim()) add({ section: 'settings', message: 'Renseignez la description générale du site.' });
  const contactEmail = draft.branding?.contactEmail?.trim();
  if (contactEmail && !/^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(contactEmail))
    add({ section: 'settings', message: 'L’adresse email publique n’est pas valide.' });
  if (draft.pages.length > 30) add({ message: 'Le site est limité à 30 pages.' });
  if (cmsMenuCount(draft.headerMenu) > 60)
    add({ section: 'navigation', message: 'Le menu est limité à 60 entrées au total.' });
  const checkMenu = (entries: readonly SiteCmsMenuItem[], depth: number): void => {
    for (const item of entries) {
      if (!item.label.trim()) add({ section: 'navigation', message: 'Chaque entrée du menu doit avoir un libellé.' });
      if (depth > 3) add({ section: 'navigation', message: 'Le menu ne peut pas dépasser trois niveaux.' });
      if (item.type === 'GROUP' && !item.children.length)
        add({ section: 'navigation', message: `La rubrique « ${item.label} » est vide.` });
      if (item.type === 'EXTERNAL_LINK') {
        try {
          const url = new URL(item.url);
          if (url.protocol !== 'https:' || url.username || url.password || /\s|\\/.test(item.url)) throw new Error();
        } catch {
          add({ section: 'navigation', message: `Le lien « ${item.label} » doit être une adresse HTTPS complète.` });
        }
      }
      checkMenu(item.children, depth + 1);
    }
  };
  checkMenu(draft.headerMenu, 1);
  const pageNames = new Set(draft.pages.map(p => p.slug));
  const privatePages = new Set(draft.pages.filter(p => p.visibility === 'DRAFT').map(p => p.slug));
  for (const page of draft.pages) {
    if (!page.title.trim()) add({ pageSlug: page.slug, message: 'Renseignez le titre de cette page.' });
    if (page.blocks.length > 40) add({ pageSlug: page.slug, message: 'Cette page dépasse 40 sections.' });
    page.blocks.forEach((block, blockIndex) => {
      const base = { pageSlug: page.slug, blockIndex };
      if ('title' in block && !block.title.trim()) add({ ...base, message: 'Renseignez le titre de la section.' });
      if ((block.type === 'heading' || block.type === 'paragraph') && !block.text.trim())
        add({ ...base, message: 'Renseignez le texte de la section.' });
      if ((block.type === 'cta' || block.type === 'button') && (!block.label.trim() || !block.href.trim()))
        add({ ...base, message: 'Renseignez le libellé et la destination du bouton.' });
      if ('text' in block && block.text.length > 5000)
        add({ ...base, message: 'Raccourcissez le texte à 5 000 caractères.' });
      if (block.type === 'info' && block.items.some(item => !item.label.trim() || !item.text.trim()))
        add({ ...base, message: 'Complétez le libellé et le texte de chaque information pratique.' });
      if (block.type === 'partners' && block.items.some(item => !item.name.trim()))
        add({ ...base, message: 'Renseignez le nom de chaque partenaire.' });
      for (const href of cmsBlockLinks(block)) {
        const target = cmsLinkedPage(href);
        if (target && !pageNames.has(target))
          add({ ...base, message: `Le lien « ${href} » ne correspond à aucune page.` });
        if (target && privatePages.has(target) && page.visibility !== 'DRAFT')
          add({
            ...base,
            publicationOnly: true,
            message: `Le lien « ${href} » pointe vers une page privée. Remplacez-le avant publication.`,
          });
        if (!cmsIsSafeLink(href)) add({ ...base, message: 'Utilisez une page interne ou une adresse HTTPS.' });
        if (href.startsWith('/documents/') && !cmsLinkedDocument(href))
          add({ ...base, message: 'Sélectionnez une destination PDF valide dans la bibliothèque.' });
      }
    });
  }
  if (JSON.stringify(draft).length > 180_000)
    add({ message: 'Le brouillon dépasse la taille maximale : réduisez le contenu avant de sauvegarder.' });
  return issues;
}
